import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, GuildBanResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface UseGuildBansReturn {
  bans: GuildBanResponse[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  unban: (banId: string) => Promise<void>;
}

export function useGuildBans(guildId: string | undefined): UseGuildBansReturn {
  const queryClient = useQueryClient();
  const bansQueryKey = queryKeys.guilds.bans(guildId!);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: bansQueryKey,
    queryFn: () => api.listGuildBans(guildId!),
    enabled: !!guildId,
  });

  const unbanMut = useMutation({
    mutationFn: (banId: string) => api.unbanGuildMember(guildId!, banId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bansQueryKey });
    },
  });

  return {
    bans: data?.bans ?? [],
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load bans' : null,
    refresh: () => {
      refetch();
    },
    unban: async (banId: string) => {
      if (!guildId) return;
      await unbanMut.mutateAsync(banId);
    },
  };
}
